import {
  Box,
  Tab,
  Tabs,
  styled,
  Button,
  Divider,
  CircularProgress,
} from '@mui/material';
import React, { memo, useCallback, useMemo, useState } from 'react';
import { Copy, Check } from '@phosphor-icons/react';
import theme from '../../theme';
import useCopyToClipboard from '../../hooks/useCopyToClipboard';
import WhatsappConversation from './WhatsappConversation';

const StyledTabs = styled(Tabs)({
  '& .MuiTabs-indicator': {
    display: 'none',
  },
  '& .MuiTab-root': {
    textTransform: 'none',
    fontWeight: 400,
    fontSize: '16px',
    letterSpacing: '-0.16px',
    padding: '8px 16px',
    color: '#6B6F80',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '10px',
    '&.Mui-selected': {
      color: theme.palette.custom.textBlue,
      fontWeight: 500,
      backgroundColor: '#E7F0FF',
      borderRadius: '6px',
    },
  },
});

interface TabPanelProps {
  children?: React.ReactNode;
  value: number;
  index: number;
}

interface ChatMessage {
  message: string;
  role: string;
  timestamp: string;
  prompt_tokens?: number;
  completion_tokens?: number;
  error?: null | string;
  latency?: number;
  status?: string;
}

export interface WhatsappChatTabsProps {
  messages: ChatMessage[];
  phoneNumber: string;
  whatsappPhonenumberId: string;
  customerName: string;
  isLoading: boolean;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return value === index ? <>{children}</> : null;
};

const WhatsappChatTabs: React.FC<WhatsappChatTabsProps> = ({
  messages = [],
  phoneNumber,
  whatsappPhonenumberId,
  isLoading,
}) => {
  const [selectedTab, setSelectedTab] = useState(0);
  const { copy } = useCopyToClipboard();
  const [transcriptCopied, setTranscriptCopied] = useState(false);

  const formatDate = useCallback((dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  }, []);

  const handleCopyTranscript = useCallback(() => {
    if (!messages || messages.length === 0) {
      return;
    }

    try {
      const transcript = messages
        .filter((message: ChatMessage) => message.message.trim() !== '')
        .map((message: ChatMessage) => {
          const role = message.role === 'user' ? 'Client' : 'Assistant';
          return `${role}: ${message.message}`;
        })
        .join('\n\n');

      copy(transcript);
      setTranscriptCopied(true);
      setTimeout(() => {
        setTranscriptCopied(false);
      }, 3000);
    } catch (error: any) {
      console.error('Failed to copy transcript', error);
    }
  }, [messages, copy]);

  const tabItems = useMemo(
    () => [
      {
        label: 'Conversation',
        desc: (
          <WhatsappConversation
            messages={messages}
            phoneNumber={phoneNumber}
            whatsappPhonenumberId={whatsappPhonenumberId}
            // customerName={customerName}
            isLoading={isLoading}
            formatDate={formatDate}
          />
        ),
      },
    ],
    [messages, phoneNumber, whatsappPhonenumberId, isLoading, formatDate]
  );

  const handleTabChange = useCallback(
    (_event: React.SyntheticEvent, newValue: number) => {
      setSelectedTab(newValue);
    },
    []
  );

  const commonBoxStyles = {
    display: 'flex',
    borderRadius: '8px',
    border: '1px solid #DCE0E7',
    backgroundColor: '#FFF',
    height: '100%',
  };

  const commonContentStyles = {
    width: '100%',
  };

  if (isLoading) {
    return (
      <Box sx={commonBoxStyles}>
        <Box
          sx={{
            ...commonContentStyles,
            p: 2,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={commonBoxStyles}>
      <Box sx={commonContentStyles}>
        <Box
          sx={{
            display: 'flex',
            width: '100%',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '10px 16px',
          }}
        >
          <StyledTabs value={selectedTab} onChange={handleTabChange}>
            {tabItems.map((tab, i) => (
              <Tab key={i} label={tab.label} />
            ))}
          </StyledTabs>

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Button
              sx={{
                display: 'flex',
                padding: '8px 16px',
                justifyContent: 'center',
                alignItems: 'center',
                borderRadius: '6px',
                border: '1px solid primary.main',
                textTransform: 'none',
              }}
              startIcon={
                transcriptCopied ? <Check size={18} /> : <Copy size={18} />
              }
              onClick={handleCopyTranscript}
              disabled={!messages || messages.length === 0}
            >
              {transcriptCopied ? 'Copied!' : 'Copy Conversation'}
            </Button>
          </Box>
        </Box>
        <Divider />
        <Box
          sx={{
            overflow: 'auto',
            flexGrow: 1,
            height: 'calc(100vh - 180px)',
            '&::-webkit-scrollbar': {
              display: 'none',
            },
            msOverflowStyle: 'none',
            scrollbarWidth: 'none',
          }}
        >
          {tabItems.map((tab, index) => (
            <TabPanel key={index} value={selectedTab} index={index}>
              {tab.desc}
            </TabPanel>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default memo(WhatsappChatTabs);
